<template>
  <div>
    <loader-dialog :visible="loading" />
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
    >
      {{ notification.text }}
    </v-snackbar>
    <v-form
      ref="form"
      v-model="ruleFormValid"
      lazy-validation
    >
      <v-autocomplete
        id="rule-form-program-autocomplete"
        v-model="form.program"
        :items="autocomplete.programs"
        :loading="autocomplete.loading"
        :search-input.sync="autocomplete.search"
        :rules="[inputRules.required]"
        :disabled="ruleId >= 0"
        :item-text="autocompleteItemText"
        class="input-max-width"
        label="Program"
        hide-no-data
        filled
        chips
        return-object
        @input="handleInput"
      />

      <v-select
        id="rule-form-transaction-type'"
        v-model="form.transactionType"
        :items="transactionTypes"
        class="input-max-width"
        label="Transaction Type"
        :rules="[inputRules.required]"
        :disabled="ruleId >= 0 || isParticipantSelected"
        filled
        dense
        @input="handleInput"
      />

      <v-text-field
        id="rule-form-time-range"
        v-model="form.timeRangeHours"
        class="input-max-width"
        filled
        label="Amount of hours"
        type="number"
        :rules="[inputRules.required, inputRules.positiveNumber, inputRules.integer, inputRules.justNumbers, inputRules.maxValue10k]"
      />

      <v-text-field
        id="rule-form-transaction-count"
        v-model="form.transactionCount"
        class="input-max-width"
        filled
        label="Amount of transactions"
        type="number"
        :rules="[inputRules.required, inputRules.positiveNumber, inputRules.integer, inputRules.justNumbers, inputRules.maxValue10k]"
      />

      <range-list-input
        id="rule-form-included-ids-ranges"
        v-model="form.includedIdsRanges"
        :range-types="rangeTypes"
        label="Inclusions"
        :check-transaction-type="validateRedemptionSelection"
        :program-id="programId"
        :transaction-type="transactionType"
      />

      <range-list-input
        id="rule-form-excluded-ids-ranges"
        v-model="form.excludedIdsRanges"
        :range-types="rangeTypes"
        label="Exclusions"
        :check-transaction-type="validateRedemptionSelection"
        :program-id="programId"
        :transaction-type="transactionType"
      />

      <v-switch
        id="rule-form-active"
        v-model="form.active"
        label="Active"
        class="active-toggle-switch"
      />

      <!-- Action buttons anchored bottom-right, separated from the Active switch to avoid misclicks -->
      <v-divider class="mt-4" />
      <div class="d-flex justify-end mt-4">
        <v-btn
          id="rule-form-button-save"
          color="primary"
          class="white--text"
          @click="saveRuleClicked"
        >
          Save
        </v-btn>
        <v-btn
          v-if="ruleId"
          id="rule-form-button-export"
          color="primary"
          class="white--text ml-2"
          @click="exportRule"
        >
          Export
        </v-btn>
        <v-btn
          v-if="ruleId"
          id="rule-form-button-import"
          color="primary"
          class="white--text ml-2"
          @click="openImportDialog()"
        >
          Import
        </v-btn>
      </div>
    </v-form>
    <v-dialog
      v-model="importDialog.show"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          Import rule
        </v-card-title>

        <v-card-text>
          <v-file-input
            v-model="importDialog.csvFile"
            accept=".csv"
            label="File input"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            :loading="importDialog.loading"
            @click="importFile()"
          >
            Import
          </v-btn>

          <v-btn
            color="primary"
            text
            :loading="importDialog.loading"
            @click="importDialog.show = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style>
.active-toggle-switch {
  max-width: fit-content;
}
.active-toggle-switch .v-label {
  pointer-events: none;
}
</style>

<script>
  import { api } from '../http/api'
  import { Routes } from '../router'

  export default {
    name: 'RuleForm',

    components: {
      RangeListInput: () => import('../components/RangeListInput'),
      LoaderDialog: () => import('../components/LoaderDialog'),
    },

    props: {
      ruleId: String,
      transactionType: String,
    },

    data: () => ({
      importDialog: {
        show: false,
        loading: false,
        csvFile: null,
      },
      loading: false,
      ruleFormValid: true,
      autocomplete: {
        programs: [],
        loading: false,
        search: null,
      },
      rangeTypes: [],
      transactionTypes: [],
      programId: null,
      isParticipantSelected: false,
      form: {
        program: null,
        transactionType: null,
        timeRangeHours: null,
        transactionCount: null,
        excludedIdsRanges: [],
        includedIdsRanges: [],
        active: true,
      },
      inputRules: {
        required: value => !!value || 'Required',
        positiveNumber: value => value > 0 || 'Must be positive number',
        integer: value => (Number.isInteger(Number(value)) && !('' + value).includes('.') && !('' + value).includes(',')) || 'Must be integer',
        justNumbers: value => !(('' + value).includes('e') || ('' + value).includes('E')) || 'Must not contain "e" character',
        maxValue10k: value => (!value || value <= 10000) || 'Max value is 10000',
      },
      notification: {
        show: false,
        text: '',
        color: 'success', // ['success', 'info', 'error']
      },
    }),

    watch: {
      'autocomplete.search' (val) {
        this.loadPrograms(val)
      },
    },

    mounted () {
      api.getTransactionTypes().then(value => {
        this.transactionTypes = value
      }).catch(err => {
        this.showErrorMessage(err)
      })
      api.getRangeTypes().then(value => {
        this.rangeTypes = value
      }).catch(err => {
        this.showErrorMessage(err)
      })
      this.loadRuleData()
    },

    methods: {
      loadRuleData () {
        if (!this.transactionType) {
          this.transactionType = sessionStorage.getItem('transactionType')
        }
        if (this.ruleId && this.transactionType) {
          this.loading = true
          sessionStorage.setItem('transactionType', this.transactionType)
          api.getRule(this.ruleId, this.transactionType).then(value => {
            this.form = value

            // load selected program for autocomplete input
            if (this.form.program) {
              this.programId = this.form.program.id
              this.loadPrograms(this.form.program.code)
            }
            // in case of redirect from NewRule to EditRule after creation of new rule we show success message
            if (this.$route.params.msg === 'created') {
              this.notification = { show: true, text: 'Rule was created.', color: 'success' }
            }
          }).catch(err => {
            this.showErrorMessage(err)
          }).finally(() => {
            this.loading = false
          })
        }
      },
      loadPrograms (term) {
        if (term) {
          this.autocomplete.loading = true
          api.getPrograms(term)
            .then(value => {
              const ids = new Set(this.autocomplete.programs.map(d => d.id))
              this.autocomplete.programs = [...this.autocomplete.programs, ...value.filter(d => !ids.has(d.id))]
            }).catch(() => {
              this.notification = { show: true, text: 'Could not load programs.', color: 'error' }
            }).finally(() => {
              this.autocomplete.loading = false
            })
        }
      },
      saveRuleClicked () {
        if (this.$refs.form.validate()) {
          if (this.ruleId) {
            this.updateRule()
          } else {
            this.createRule()
          }
        }
      },
      createRule () {
        this.loading = true
        api.createRule(this.form).then(value => {
          // rule was created, now redirect to EditRule, since we are on NewRule and rule now exists
          this.$router.push(
            {
              name: Routes.EDIT_RULE,
              params: { ruleId: value.id + '', transactionType: value.transactionType, msg: 'created' }, // success message will be shown on mount of EditRule view
            })
        }).catch(err => {
          this.showErrorMessage(err)
        }).finally(() => {
          this.loading = false
        })
      },
      updateRule () {
        this.loading = true
        api.updateRule(this.ruleId, this.form).then(value => {
          this.form = value
          this.notification = { show: true, text: 'Rule was updated.', color: 'success' }
        }).catch(err => {
          this.showErrorMessage(err)
        }).finally(() => {
          this.loading = false
        })
      },
      autocompleteItemText: item => item.code + ' — ' + item.description,
      showErrorMessage (err) {
        if (err.data?.message) {
          this.notification = { show: true, text: err.data.message, color: 'error' }
        } else {
          this.notification = { show: true, text: 'Something went wrong.', color: 'error' }
        }
      },
      exportRule () {
        if (this.ruleId && this.transactionType) {
          this.loading = true
          api.exportRule(this.ruleId, this.transactionType).then().catch(err => {
            this.showErrorMessage(err)
          }).finally(() => {
            this.loading = false
          })
        }
      },
      openImportDialog () {
        this.importDialog.show = true
      },
      importFile () {
        if (this.importDialog.csvFile) {
          const formData = new FormData()
          formData.append('file', this.importDialog.csvFile, this.importDialog.csvFile.name)

          api.importRule(this.ruleId, this.transactionType, formData).then(value => {
            this.loadRuleData()
            this.notification = { show: true, text: 'Rule was updated.', color: 'success' }
          }).catch(err => {
            this.showErrorMessage(err)
          }).finally(() => {
            this.importDialog.loading = false
            this.importDialog.show = false
          })
        }
      },
      validateRedemptionSelection (transactionType) {
        let isParticipantInInclusion = false
        let isParticipantInExclusion = false
        if (transactionType === 'Redemption') {
          for (let i = 0; i < this.form.includedIdsRanges.length; i++) {
            if (this.form.includedIdsRanges[i].participant) {
              isParticipantInInclusion = true
              break
            }
          }
          for (let i = 0; i < this.form.excludedIdsRanges.length; i++) {
            if (this.form.excludedIdsRanges[i].participant) {
              isParticipantInExclusion = true
              break
            }
          }
        }
        this.isParticipantSelected = isParticipantInInclusion || isParticipantInExclusion
      },
      handleInput () {
        this.programId = this.form.program ? this.form.program.id : null
        this.transactionType = this.form.transactionType ? this.form.transactionType : null
      },
    },
  }
</script>

<style scoped>
  .input-max-width {
    max-width: 500px;
  }
</style>
